<template>
  <div class="form-client-details">
    <p
      class="insurance-chosen-variant__subtitle">
      Стоимость полиса
    </p>
    <SummaryCard
      :tariffs="tariffs"
      :new-tariffs="newTariffs"
      :countCars="countCars"
      :contract-term="contractTerm"
      :quote-status="quoteStatus"
    />
    <ElForm class="form-client-details__form form form-result"
            ref="resultFormRef"
            :model="resultForm"
            :rules="rules"
    >
      <div class="form__item form__item--col" :class="{ 'policy-dates--short-term': isShortTerm }">
        <ElFormItem class="form__col form__col--half"
                    prop="police_date">
          <ElDatePicker v-input-id="'result-policy-start-date'"
                        v-model="resultForm.police_date"
                        v-mask="{mask: '9{2}.9{2}.9{4}'}"
                        ref="policeDatePicker"
                        :disabled="!!lockedStartDate"
                        type="date"
                        placeholder="дд.мм.гггг"
                        format="dd.MM.yyyy"
                        value-format="dd.MM.yyyy"
                        :picker-options="{ disabledDate: (time) => disableInvalidDatePolice(time) }"
          >
          </ElDatePicker>
          <span class="form__label">Дата начала полиса</span>
        </ElFormItem>

        <ElFormItem v-if="isShortTerm"
                    class="form__col form__col--half"
                    prop="end_police_date">
          <ElDatePicker v-input-id="'result-policy-end-date'"
                        v-model="resultForm.end_police_date"
                        type="date"
                        placeholder="дд.мм.гггг"
                        format="dd.MM.yyyy"
                        value-format="dd.MM.yyyy"
                        :disabled="!parsePolicyDate(resultForm.police_date)"
                        :picker-options="{ disabledDate: disablePolicyEndDate }"
          />
          <span class="form__label">Дата окончания полиса</span>
        </ElFormItem>
      </div>

      <p class="date-start-notify">
        Договор вступает в силу со дня, следующего за днем оплаты. В случае, если полис оплачивается не в
        день оформления мы изменим дату начала полиса самостоятельно.
      </p>
      <p
        v-if="!!policyDuration"
        class="insurance-chosen-variant__subtitle police-date">
        Ваш полис будет действовать {{ policyDuration }}
      </p>

      <div class="form__buttons form__buttons--margin-small">
        <BaseButton text="Назад"
                    theme="muted"
                    is-text
                    size="large"
                    @click.native="$emit('prev-step')"
        />
        <BaseButton text="Оформить полис"
                    :is-disabled="quoteLoading"
                    size="large"
                    @click.native="onSubmit($refs.resultFormRef)"/>

      </div>
    </ElForm>
  </div>
</template>

<script>
import {addYears, parse, addDays, differenceInCalendarDays, format, isValid, startOfDay} from 'date-fns';
import BaseButton from '@/components/ui/BaseButton';
import SummaryCard from '@/components/ui/SummaryCard';
import ModalPolicyIssuanceError from '@/components/modals/ModalPolicyIssuanceError';

export default {
  name: 'FormResult',
  components: {
    BaseButton,
    ModalPolicyIssuanceError,
    SummaryCard,
  },
  props: {
    policyDates: {type: Object, required: true},
    quoteStatus: String,
    quoteLoading: Boolean,
    lockedStartDate: String,
    isShortTerm: Boolean,
    tariffs: Array,
    newTariffs: Array,
    countCars: Number,
    prolongationData: Object,
    minStartDateTimestamp: Number,
  },
  watch: {
    lockedStartDate: {
      immediate: true,
      handler(value) {
        if (value) {
          this.resultForm.police_date = this.lockedStartDate || value;
        }
      },
    },
    'resultForm.police_date'() {
      if (this.isShortTerm && this.resultForm.end_police_date) {
        this.$nextTick(() => this.$refs.resultFormRef.validateField('end_police_date'));
      }
    },
    policyPeriod: {
      immediate: true,
      handler(value) {
        this.$emit('policy-period-change', value);
      },
    },
    minStartDateTimestamp(newValue, oldValue) {
      if (newValue && !this.lockedStartDate) {
        let jsTimestamp = newValue * 1000;

        let date = new Date(jsTimestamp);
        this.resultForm.police_date = date.toLocaleDateString("ru-RU");
      }
    },
    prolongationData(newValue, oldValue) {
      if (this.lockedStartDate) {
        this.resultForm.police_date = this.lockedStartDate;
        return;
      }
      const currentDate = new Date();

      if (newValue?.dateEndTimestamp) {
        let jsTimestamp = newValue?.dateEndTimestamp * 1000;
        let endDate = new Date(jsTimestamp);
        if (endDate > currentDate) {
          let newDateStart = (endDate.setDate(endDate.getDate() + 1));
          const policeDate = new Date(newDateStart);
          this.resultForm.police_date = policeDate.toLocaleDateString("ru-RU");
        }
      }
    },
  },
  data() {
    return {
      resultForm: this.policyDates,
      rules: {
        end_police_date: [
          {validator: this.validatePolicyEndDate, trigger: ['blur', 'change']},
        ],
        police_date: [
          {validator: this.validatePolicyStartDate, trigger: ['blur', 'change']},
        ],
      },
      innErrorText: '',
      innIsDefined: false,
      showErrorModal: false,
    }
  },
  mounted() {
    const currentDate = new Date();
    let newDateStart = (currentDate.setDate(currentDate.getDate() + 1));
    if (this.prolongationData?.dateEndTimestamp) {
      let endProlongTimestamp = this.prolongationData?.dateEndTimestamp * 1000;
      let endProlongDate = new Date(endProlongTimestamp);

      if (endProlongDate > currentDate) {
        newDateStart = (endProlongDate.setDate(endProlongDate.getDate() + 1));
      }
    }

    if (!this.resultForm.police_date) {
      const policeDate = new Date(newDateStart);
      this.resultForm.police_date = policeDate.toLocaleDateString("ru-RU");
    }
  },
  methods: {
    parsePolicyDate(value) {
      if (typeof value !== 'string' || !/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
        return null;
      }
      const date = parse(value, 'dd.MM.yyyy', new Date());
      return isValid(date) && format(date, 'dd.MM.yyyy') === value ? date : null;
    },
    disablePolicyEndDate(date) {
      const start = this.parsePolicyDate(this.resultForm.police_date);
      return !start || differenceInCalendarDays(date, start) < 0 || differenceInCalendarDays(date, start) > 364;
    },
    validatePolicyStartDate(rule, value, callback) {
      if (this.lockedStartDate) {
        return callback(value === this.lockedStartDate ? undefined : new Error('Дата начала должна быть следующим днём после окончания продлеваемого договора'));
      }
      const date = this.parsePolicyDate(value);
      if (!date) {
        return callback(new Error('Введите корректную дату начала полиса'));
      }
      if (this.disableInvalidDatePolice(date)) {
        return callback(new Error('Выберите доступную дату начала полиса в календаре'));
      }
      callback();
    },
    validatePolicyEndDate(rule, value, callback) {
      if (!this.isShortTerm) {
        return callback();
      }
      const date = this.parsePolicyDate(value);
      if (!date) {
        return callback(new Error('Введите корректную дату окончания полиса'));
      }
      if (this.disablePolicyEndDate(date)) {
        return callback(new Error('Дата окончания должна быть не раньше даты начала, срок полиса — не более 365 дней включительно'));
      }
      callback();
    },
    async onSubmit(formEl) {
      formEl.validate(valid => {
        if (valid) {
          this.resultForm.end_police_date = this.getEndPoliceDate;
          this.$emit('result-submit', this.resultForm);
        }
      })
    },
    disableInvalidDatePolice(calendarDate) {
      if (this.isShortTerm) {
        return startOfDay(calendarDate) < addDays(startOfDay(new Date()), 1);
      }
      let currentDate = new Date();

      if (this.prolongationData?.dateEndTimestamp) {
        let endProlongTimestamp = this.prolongationData?.dateEndTimestamp * 1000;
        let endProlongDate = new Date(endProlongTimestamp);

        if (endProlongDate > currentDate) {
          currentDate = endProlongDate;
        }
      }

      let currentDate60 = new Date();
      currentDate60.setDate(currentDate60.getDate() + 60);

      return !(calendarDate.getTime() > currentDate.getTime() && calendarDate.getTime() < currentDate60.getTime());
    },
  },
  computed: {
    contractTerm() {
      return this.isShortTerm ? (this.policyDuration || '—') : '12 месяцев';
    },
    policyDuration() {
      const start = this.parsePolicyDate(this.resultForm.police_date);
      const end = this.parsePolicyDate(this.getEndPoliceDate);
      if (!start || !end || end < start || (this.isShortTerm && this.disablePolicyEndDate(end))) {
        return '';
      }
      const days = differenceInCalendarDays(end, start) + 1;
      const plural = new Intl.PluralRules('ru').select(days);
      const unit = {one: 'день', few: 'дня', many: 'дней', other: 'дня'}[plural];
      return `${days} ${unit}`;
    },
    policyPeriod() {
      return {
        startDate: this.resultForm.police_date,
        endDate: this.getEndPoliceDate,
        contractTerm: this.contractTerm,
      };
    },
    getEndPoliceDate() {
      if (this.isShortTerm) {
        return this.parsePolicyDate(this.resultForm.end_police_date) ? this.resultForm.end_police_date : '';
      }
      const startPoliceDate = this.parsePolicyDate(this.resultForm.police_date);
      if (!startPoliceDate) {
        return '';
      }
      let endPoliceDate = (startPoliceDate.setFullYear(startPoliceDate.getFullYear() + 1));
      endPoliceDate = new Date(endPoliceDate);
      endPoliceDate = (endPoliceDate.setDate(endPoliceDate.getDate() - 1));
      endPoliceDate = new Date(endPoliceDate);

      return endPoliceDate.toLocaleDateString("ru-RU");
    },
    passportIssueMinDate() {
      if (!this.clientForm.birth_date) {
        return '';
      }
      const birthDate = parse(this.clientForm.birth_date, 'dd.MM.yyyy', new Date());
      return addYears(birthDate, 14)
    },
  },
}
</script>

<style scoped lang=scss>
.policy-dates--short-term {
  @media #{$screen-small} {
    margin-bottom: rem(24px);
  }
}

.el-radio-group {
  @include text-medium($font-weight-regular, $line-height-medium);
  font-family: $font-family-secondary;
  margin-top: rem($gap-normal);
  margin-bottom: rem($gap-medium);
}

.insurance-chosen-variant {
  &__subtitle {
    @include text-medium($font-weight-regular, $line-height-medium);

    font-family: $font-family-secondary;

    margin-top: rem($gap-normal);
    margin-bottom: rem($gap-medium);
    width: fit-content;

    color: $color-text-muted-low2;

    @media #{$screen-big} {
      font-size: rem($font-size-normal);
    }
  }
}

.form-result {
  margin-top: 1.5rem;
}

.police-date {
  font-size: $font-size-small;
}

.date-start-notify {
  font-size: 10px;
  margin-top: 5px;
}
</style>
